"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDefaultCurrencySymbol = exports.formatMoney = exports.getMonetaryFields = void 0;
// tslint:disable-next-line:max-line-length
var getDefaultLocaleForCurrencySymbol_1 = require("./getDefaultLocaleForCurrencySymbol");
var getPolyfillDefaultSymbolForCurrency_1 = require("./getPolyfillDefaultSymbolForCurrency");
var DEFAULT_LOCALE_FOR_DECIMAL_PLACES = 'en-US';
var DEFAULT_CURRENCY_AMOUNT_FOR_SYMBOL = 100;
var DEFAULT_CURRENCY_SYMBOL = '$';
/**
 * returns a MonetaryFields object that contains localized currency amounts
 *
 * @param unitAmount amount in lowest demonization currency
 * @param currency currency code of unit amount
 * @param userLocale locale of user, affects formatting
 * @param concise if possible, truncate decimal places for decimal currencies.
 */
var getMonetaryFields = function (props) {
    var defaultFractionDigits = getDecimalPlacesForCurrency(props.currency);
    var moneyAmountWithDecimalPlaces = props.unitAmount / Math.pow(10.0, defaultFractionDigits);
    var displayDigits = props.concise && props.unitAmount % Math.pow(10, defaultFractionDigits) === 0
        ? 0
        : defaultFractionDigits;
    var numberFormatter = new Intl.NumberFormat(props.userLocale, {
        style: 'currency',
        currency: props.currency,
        minimumFractionDigits: displayDigits,
        maximumFractionDigits: displayDigits,
    });
    var displayString = numberFormatter.format(moneyAmountWithDecimalPlaces);
    if (props.includeUnits === false) {
        var currencyOptions = numberFormatter.resolvedOptions();
        displayString = moneyAmountWithDecimalPlaces.toLocaleString(props.userLocale, __assign(__assign({}, currencyOptions), { style: 'decimal' }));
    }
    var symbolPlacement = 'left';
    var decimalPart = [];
    if (typeof numberFormatter.formatToParts === 'function') {
        var currencyInParts = numberFormatter.formatToParts(moneyAmountWithDecimalPlaces);
        decimalPart = currencyInParts.filter(function (part) { return part.type === 'decimal'; });
        if (currencyInParts.length > 0) {
            symbolPlacement =
                currencyInParts[0].type === 'currency' ? 'left' : 'right';
        }
    }
    return {
        displayString: displayString,
        symbolPlacement: symbolPlacement,
        unitAmount: props.unitAmount,
        currency: props.currency,
        decimalPlaces: defaultFractionDigits,
        decimalSymbol: decimalPart.length ? decimalPart[0].value : undefined,
        symbol: (0, exports.getDefaultCurrencySymbol)(props.currency),
    };
};
exports.getMonetaryFields = getMonetaryFields;
/**
 * returns a localized currency display string for the given locale in the
 * provided currency.
 *
 * @param unitAmount amount in lowest demonization currency
 * @param currency currency code of unit amount
 * @param userLocale locale of user, affects formatting
 * @param concise if possible, truncate decimal places for decimal currencies.
 */
var formatMoney = function (props) {
    return (0, exports.getMonetaryFields)(props).displayString;
};
exports.formatMoney = formatMoney;
/**
 * returns a currency symbol for a given currency, ie "$".
 * should only be used in determining "priciness", ie "$$$"
 *
 * @param currencyCode currency code to get symbol for
 */
var getDefaultCurrencySymbol = function (currencyCode) {
    var defaultLocaleForCurrency = (0, getDefaultLocaleForCurrencySymbol_1.getDefaultLocaleForCurrencySymbol)(currencyCode);
    var numberFormatter = Intl.NumberFormat(defaultLocaleForCurrency, {
        currency: currencyCode,
        style: 'currency',
    });
    if (typeof numberFormatter.formatToParts !== 'function') {
        return (0, getPolyfillDefaultSymbolForCurrency_1.getPolyfillDefaultSymbolForCurrency)(currencyCode);
    }
    return getSymbolFromFormattedStringArray(numberFormatter.formatToParts(DEFAULT_CURRENCY_AMOUNT_FOR_SYMBOL));
};
exports.getDefaultCurrencySymbol = getDefaultCurrencySymbol;
/**
 * Intl.NumberFormat.formatToParts() returns an array of
 * Intl.NumberFormParts, each part contains a type and value.
 *
 * Type can be:
 *  - integer
 *  - decimal
 *  - fraction
 *  - literal
 *  - currency
 *
 * we want to get the value of type "currency", which is the currency symbol
 * designation
 *
 * @param numberFormatPartArray list of Intl.NumberFormatPart that breaks
 * down a currency formatted string
 */
var getSymbolFromFormattedStringArray = function (numberFormatPartArray) {
    var currencyNumberFormatPart = numberFormatPartArray.find(function (part) { return part.type === 'currency'; });
    return currencyNumberFormatPart !== null
        ? currencyNumberFormatPart.value
        : DEFAULT_CURRENCY_SYMBOL;
};
/**
 * returns the default amount of decimal places for a given currency.
 * relies on creating an Intl.NumberFormat and pulling out the
 * minimum fraction digits for the resulting object
 *
 * @param currencyCode currency code to get default decimal places for
 */
var getDecimalPlacesForCurrency = function (currencyCode) {
    var numberFormatter = Intl.NumberFormat(DEFAULT_LOCALE_FOR_DECIMAL_PLACES, {
        currency: currencyCode,
        style: 'currency',
    });
    return numberFormatter.resolvedOptions().minimumFractionDigits || 0;
};
